import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../Assets/Logo-cyqLkda7.jpeg";
import { Mail, Lock, Eye, EyeOff, LogIn, Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { senvraApi } from "../utils/axiosInstance";
import { useAuth } from "../utils/AuthProvider";

const Login = () => {
  const navigate = useNavigate();
  const { auth, setAuth } = useAuth();
  
  // State to manage which UI to show: "login" or "reset" (OTP)
  const [view, setView] = useState("login");
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otp: "",
    newPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  // Separate visibility states for each password field
  const [showPasswords, setShowPasswords] = useState({
    login: false,
    reset: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Form field validations based on current view
  const validate = (values, currentView = view) => {
    let errors = {};

    if (!values.email) {
      errors.email = "Email Address is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (currentView === "login") {
      if (!values.password) {
        errors.password = "Password is required";
      }
    }

    if (currentView === "reset") {
      if (!values.otp) {
        errors.otp = "OTP is required";
      }
      if (!values.newPassword) {
        errors.newPassword = "New password is required";
      } else if (values.newPassword.length < 6) {
        errors.newPassword = "Password must be at least 6 characters";
      }
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validate({ ...formData, [name]: value })[name],
    }));
  };

  const handleForgotPassword = async () => {
    setServerError("");
    setSuccessMessage("");
    
    // Validate email before sending OTP
    if (!formData.email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: "Please enter a valid email to send OTP" }));
      setTouched((prev) => ({ ...prev, email: true }));
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await senvraApi.post('/auth/otp/request', {
        email: formData.email
      });
      
      setSuccessMessage(response.data?.message || "OTP sent successfully");
      // Automatically switch to reset view to enter OTP
      setView("reset");
    } catch (error) {
      console.error('Forgot password error:', error);
      setServerError(error.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleBackToLogin = () => {
    setServerError("");
    setSuccessMessage("");
    setErrors({});
    setTouched({});
    setView("login");
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setSuccessMessage("");

    const newErrors = validate(formData, "login");
    setErrors(newErrors);
    setTouched({ email: true, password: true });

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      try {
        const response = await senvraApi.post('/auth/login', {
          email: formData.email.toLowerCase(),
          password: formData.password
        });

        const { access_token, refresh_token, user } = response.data;

        if (access_token && refresh_token && user) {
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          localStorage.setItem('user', JSON.stringify(user));
          setAuth(true);

          setSuccessMessage(`Welcome back, ${user.name}! Redirecting...`);
          
          setTimeout(() => {
            navigate('/dashboards', { replace: true });
          }, 1500);
        } else {
          setServerError("Invalid response from server");
        }
      } catch (error) {
        console.error('Login error:', error);
        if (error.response) {
          const errorMessage = error.response.data?.detail || 
                               error.response.data?.message || 
                               'Invalid email or password';
          setServerError(errorMessage);
        } else if (error.request) {
          setServerError('Network error. Please check your connection.');
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handler for OTP Reset Flow
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setSuccessMessage("");

    const newErrors = validate(formData, "reset");
    setErrors(newErrors);
    setTouched({ email: true, otp: true, newPassword: true });

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      try {
        const response = await senvraApi.post('/auth/password/reset', {
          email: formData.email.toLowerCase(),
          otp: formData.otp,
          new_password: formData.newPassword
        });

        setSuccessMessage(response.data?.message || "Password reset successfully. You can now login.");
        
        setTimeout(() => {
          setFormData(prev => ({ ...prev, password: "", otp: "", newPassword: "" }));
          setView("login");
          setSuccessMessage("");
        }, 2500);

      } catch (error) {
        console.error('Reset password error:', error);
        setServerError(error.response?.data?.message || "Invalid OTP or failed to reset password.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div style={{ marginTop: "75px" }} className="w-full max-w-[480px]">
        {/* Top Icon */}
        <div className="flex justify-center mb-2">
          <div className="w-16 h-16 bg-[#0091ea] text-white rounded-2xl flex items-center justify-center shadow-lg shadow-sky-100 transition-all">
            {view === "login" ? <LogIn size={32} /> : <KeyRound size={32} />}
          </div>
        </div>

        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-['Libre_Baskerville'] text-[#1e293b] dark:text-white mb-2 tracking-tight">
            {view === "login" ? "Welcome Back" : "Reset Password"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
            {view === "login" ? (
              <>
                Or{" "}
                <button
                  onClick={() => navigate("/register")}
                  className="text-[#0091ea] font-bold hover:underline transition"
                >
                  create a new account for free
                </button>
              </>
            ) : (
              "Enter your details to create a new password"
            )}
          </p>
        </div>

        {/* Success Message Toaster */}
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-2xl text-center text-sm font-bold">
            ✅ {successMessage}
          </div>
        )}

        {/* Error Alert Toaster */}
        {serverError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-center text-sm font-bold animate-shake">
            ⚠️ {serverError}
          </div>
        )}
        
        <div className="bg-white dark:bg-slate-900 rounded-[32px] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden relative p-8 md:p-10">
          
          {/* ================= LOGIN VIEW ================= */}
          {view === "login" && (
            <form onSubmit={handleLoginSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-[15px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="name@example.com"
                    className={`w-full pl-12 pr-4 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                      touched.email && errors.email ? "ring-red-400" : "focus:ring-[#0091ea]"
                    }`}
                  />
                </div>
                {touched.email && errors.email && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠️</span> {errors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-[12px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                  Password
                </label>
                <div className="relative group">
                  <Lock size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                  <input
                    type={showPasswords.login ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="••••••••"
                    className={`w-full pl-12 pr-12 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                      touched.password && errors.password ? "ring-red-400" : "focus:ring-[#0091ea]"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('login')}
                    className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600 transition"
                  >
                    {showPasswords.login ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠️</span> {errors.password}
                  </p>
                )}
              </div>

              {/* Forgot Password Actions */}
              <div className="flex items-center justify-end pt-1">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isSendingOtp}
                  className="text-[#0091ea] font-bold hover:underline transition text-sm flex items-center gap-2 disabled:opacity-70"
                >
                  {isSendingOtp && <Loader2 size={14} className="animate-spin" />}
                  Forgot your password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-full font-black text-base bg-[#0091ea] text-white transition-all hover:bg-[#0081d5] hover:shadow-xl hover:shadow-sky-100 disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="animate-spin" size={20} /> Signing In...</>
                ) : (
                  "Sign In →"
                )}
              </button>
            </form>
          )}

          {/* ================= FORGOT PASSWORD (OTP) VIEW ================= */}
          {view === "reset" && (
            <form onSubmit={handleResetSubmit} className="space-y-6">
              <div>
                <label className="block text-[15px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="name@example.com"
                    className={`w-full pl-12 pr-4 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                      touched.email && errors.email ? "ring-red-400" : "focus:ring-[#0091ea]"
                    }`}
                  />
                </div>
                {touched.email && errors.email && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠️</span> {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                  OTP Code
                </label>
                <div className="relative group">
                  <KeyRound size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                  <input
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter 6-digit OTP"
                    className={`w-full pl-12 pr-4 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                      touched.otp && errors.otp ? "ring-red-400" : "focus:ring-[#0091ea]"
                    }`}
                  />
                </div>
                {touched.otp && errors.otp && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠️</span> {errors.otp}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-black text-slate-700 dark:text-slate-300 mb-2 tracking-widest">
                  New Password
                </label>
                <div className="relative group">
                  <Lock size={18} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-[#0091ea]" />
                  <input
                    type={showPasswords.reset ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="••••••••"
                    className={`w-full pl-12 pr-12 py-4 rounded-xl border-none bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all focus:ring-2 ${
                      touched.newPassword && errors.newPassword ? "ring-red-400" : "focus:ring-[#0091ea]"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('reset')}
                    className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600 transition"
                  >
                    {showPasswords.reset ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {touched.newPassword && errors.newPassword && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠️</span> {errors.newPassword}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-4 pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-full font-black text-base bg-[#0091ea] text-white transition-all hover:bg-[#0081d5] hover:shadow-xl hover:shadow-sky-100 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <><Loader2 className="animate-spin" size={20} /> Updating...</>
                  ) : (
                    "Update Password"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full py-3 rounded-full font-bold text-sm bg-transparent text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={16} /> Back to Login
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;